import React, { useEffect, useState, useRef } from 'react';
import { Button, Col, Container, Row } from 'react-bootstrap';
import { Field } from 'formik';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { generateKYCFlowStatus, hasPermissions, isAdminView, toTitleCase } from '../../utils';

import { useAuth } from '../context';

import FieldWrapper from '../form/wrappers/Field';
import TooltipButton from '../common/TooltipButton';

import ViewToEditForm from '../form/ViewToEditForm';
import { KYC_ADDED_OPTIONS, KYC_STANDALONE_OPTION } from '../../constants';

export default function KYCFlowForm({
  flow,
  options,
  clear,
  setCreateFlow,
  editFlow,
  deleteFlow,
  isEditing,
  setIsEditing,
  isCreate,
  readonly,
}) {
  const { t } = useTranslation();
  const { user } = useAuth();

  const formRef = useRef(null);

  const formValidationSchema = yup.object().shape({
    name: yup
      .string()
      .max(254, t('common:form.errors.max', { count: 254 }))
      .required(
        t('common:form.errors.required', { field: t(`applications.kycFlows.formFields.name`) })
      ),
    selected_standalone_option: yup
      .mixed()
      .nullable()
      .required(
        t('common:form.errors.required', {
          field: t(`applications.kycFlows.formFields.selected_standalone_option`),
        })
      ),
  });
  const [validOptions, setValidOptions] = useState([]);

  const formContents = ({ values }) => {
    const generateValidOptionList = () => {
      if (!Object.values(options).length) return [];
      if (!values[KYC_STANDALONE_OPTION]) return [];

      if (values[KYC_STANDALONE_OPTION] !== flow[KYC_STANDALONE_OPTION]) {
        // eslint-disable-next-line no-param-reassign
        values[KYC_ADDED_OPTIONS] = [];
      }

      let is_kyb = false;
      const standaloneOption = values[KYC_STANDALONE_OPTION];
      if (standaloneOption && standaloneOption.toLowerCase().includes('kyb')) is_kyb = true;
      if (is_kyb) return options.kyb_options;
      return options.kyc_options;
    };

    useEffect(() => {
      const optionsToSet = generateValidOptionList();
      setValidOptions(optionsToSet);
    }, [values[KYC_STANDALONE_OPTION]]);

    return (
      <Container>
        <Row className="mx-2 mt-3">
          <Col md={8} className="ps-0">
            <CustomTextField
              flow={flow}
              value="name"
              isEditing={isEditing && flow.version === 1}
              t={t}
              readonly={flow.isNext}
              toolTipText={t('applications.kycFlows.formFields.name_tooltip')}
            />
          </Col>
          <Col md={4} className="ps-0">
            <CustomTextField flow={flow} value="version" readonly t={t} />
          </Col>
        </Row>
        {!isCreate && (
          <Row className="mx-2">
            <Col md={12} className="ps-0">
              <CustomTextField flow={flow} value="uuid" readonly t={t} />
            </Col>
          </Row>
        )}
        <Row className="mx-2 mt-3">
          <Col md={12} className="ps-0">
            <CustomSelectField
              flow={flow}
              value={KYC_STANDALONE_OPTION}
              options={options ? options.standalone_options : []}
              isEditing={isEditing}
              i
              t={t}
              isCreate={false}
            />
          </Col>
        </Row>
        {values[KYC_STANDALONE_OPTION] && (
          <Row className="mx-2">
            <Col md={12} className="ps-0 mt-2">
              <CustomMultiSelectField
                values={values}
                key={KYC_ADDED_OPTIONS}
                flow={flow}
                value={KYC_ADDED_OPTIONS}
                options={validOptions}
                isEditing={isEditing}
                t={t}
                isCreate={false}
                toolTipText={t(
                  'applications.kycFlows.formFields.selected_additional_options_tooltip'
                )}
              />
            </Col>
          </Row>
        )}
      </Container>
    );
  };

  const emptyForm = (
    <div className="text-muted m-4 text-center">{t('applications.kycFlows.noFlowSelected')}</div>
  );

  const kycForm = (
    <div className="mb-4">
      <ViewToEditForm
        initialValues={flow}
        uneditedValues={{}}
        formValidationSchema={formValidationSchema}
        formContents={formContents}
        t={t}
        handleUpdate={editFlow}
        handleDelete={() => deleteFlow(flow.uuid)}
        canUpdateItem={hasPermissions(user, 'kyc', 'Write')}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
        deleteMessage={t('common:form.messages.confirm.delete', { name: flow.name || '' })}
        readonly={readonly || !generateKYCFlowStatus(flow).canEdit}
        formRef={formRef}
        hideForm={clear}
        isCreate={isCreate}
      />
    </div>
  );

  return (
    <div className="border rounded mb-4">
      <div className="d-flex justify-content-between align-items-center">
        <h4 className="m-4">KYC Flow Details</h4>
        {!isCreate && !isAdminView() && !readonly && (
          <Button
            onClick={() => setCreateFlow(flow.approved, flow)}
            className="generate me-0 rounded-1 py-1 m-4 me-3"
            variant="primary"
            size="sm"
            disabled={!Object.values(options).length}
          >
            {flow.approved ? t(`common:buttons.addNewVersion`) : t(`common:buttons.addNew`)}
          </Button>
        )}
      </div>
      {isEditing && (
        <div className="text-muted mb-4 ms-4">{t(`applications.kycFlows.description`)}</div>
      )}
      {flow && Object.values(flow).length === 0 ? emptyForm : kycForm}
    </div>
  );
}

function CustomSelectField({ flow, value, options, isEditing, t, toolTipText = '' }) {
  return (
    <Field
      className={`${value}`}
      id={`editKYCFlowForm.${value}`}
      name={`${value}`}
      type={isEditing ? 'select' : 'text'}
      disabled={!isEditing}
      options={Object.values(options).map((option) => ({
        label: toTitleCase(option.name),
        value: option.name,
      }))}
      label={
        <>
          {t(`applications.kycFlows.formFields.${value}`)}
          {toolTipText !== '' && (
            <TooltipButton
              placement="top"
              variant="link"
              text={toolTipText}
              className="ms-2 no-underline p-0"
            >
              <i className="sila-icon info text-lg" />
            </TooltipButton>
          )}
        </>
      }
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...(!isEditing && { value: toTitleCase(flow[value]) })}
      component={FieldWrapper}
    />
  );
}

function CustomMultiSelectField({ flow, value, options, isEditing, t, values, toolTipText = '' }) {
  let selectedData = new Set();
  let optionNames = '';

  if (values[KYC_STANDALONE_OPTION] && Object.values(options).length !== 0) {
    const optionKey = values[KYC_STANDALONE_OPTION];
    const optionData = options[optionKey];

    if (optionData) {
      selectedData = new Set(optionData.required_data);
      optionNames = optionData.required_data.map((name) => toTitleCase(name)).join(', ');
    }
  }

  if (flow[KYC_ADDED_OPTIONS].length) {
    optionNames += `, ${flow[KYC_ADDED_OPTIONS].map((name) => toTitleCase(name)).join(', ')}`;
  }

  const fieldLabel = (
    <>
      {t(`applications.kycFlows.formFields.${value}`)}
      {toolTipText !== '' && (
        <TooltipButton
          placement="top"
          variant="link"
          text={toolTipText}
          className="ms-2 no-underline p-0"
        >
          <i className="sila-icon info text-lg" />
        </TooltipButton>
      )}
    </>
  );

  const textField = (
    <Field
      className={`${value}`}
      id={`editKYCFlowForm.${value}`}
      name={`${value}`}
      type="elastic"
      value={optionNames}
      disabled
      label={fieldLabel}
      component={FieldWrapper}
    />
  );

  const checkboxField = (
    <div role="group" aria-labelledby="checkbox-group">
      <div className="form-label">{fieldLabel}</div>
      <Row>
        {Object.values(options)
          .filter((option) => !option.standalone)
          .map((option) => (
            <Col md={6} lg={4} xl={3} key={option.name} className="my-2">
              <label htmlFor={option.name}>
                <Field
                  type="checkbox"
                  name={value}
                  value={option.name}
                  className="mx-2"
                  disabled={selectedData.has(option.name)}
                  checked={selectedData.has(option.name) || values[value].includes(option.name)}
                />
                {toTitleCase(option.name)}
              </label>
            </Col>
          ))}
      </Row>
    </div>
  );
  return isEditing ? checkboxField : textField;
}

function CustomTextField({ flow, value, isEditing, t, toolTipText = '' }) {
  const [hover, setHover] = useState(false);
  const generateTextStyle = () => {
    if (hover) return { textOverflow: 'clip' };
    return {};
  };
  return (
    <div onMouseEnter={() => setHover(true)}>
      <Field
        className={`${value}`}
        style={generateTextStyle()}
        id={`editLimitForm.${flow.uuid}.${value}`}
        name={`${value}`}
        type="text"
        placeholder={flow[value]}
        disabled={!isEditing}
        label={
          <>
            {t(`applications.kycFlows.formFields.${value}`)}
            {toolTipText !== '' && (
              <TooltipButton
                placement="top"
                variant="link"
                text={toolTipText}
                className="ms-2 no-underline p-0"
              >
                <i className="sila-icon info text-lg" />
              </TooltipButton>
            )}
          </>
        }
        component={FieldWrapper}
      />
    </div>
  );
}
